import { OnMutateFn } from '@diritto/common/base/mutateFunction';
import styled from '@emotion/styled';
import { ReplyEditAction } from 'constants/reply';
import { useEpisodeReplyDispatch, useEpisodeReplyState } from 'contexts/EpisodeReplyProvider';
import { BlockedOrDeletedReply } from 'app-components/reply/BlockedOrDeletedReply';
import { Reply } from 'app-components/reply/Reply';
import React, { useCallback } from 'react';
import { useBlockProfiles } from 'state-hooks/accounts/useBlockProfiles';
import { EmptyList } from 'ui-components/common/EmptyList';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodePublicRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import {
  ReplyBlockedRelayDto,
  ReplyDeletedRelayDto,
  ReplyRelayDto,
} from '@diritto/common/relay/dtos/episode-reply-relay.dto';

interface RepliesProps {
  className?: string;
  replies: ReplyRelayDto[];
  profiles?: Record<number, ProfileLitePublicRelayDto>;
  contents?: Record<number, ContentPublicRelayDto>;
  episodes?: Record<number, EpisodePublicRelayDto>;
  focusedReplyId?: number;
  likeReplyIds?: number[];
  onClickReReply?: (reply: ReplyRelayDto) => void;
  onClickReply?: (reply: ReplyRelayDto) => void;
  parent?: number;
  myReplyEditable?: boolean;
  myReplyDeletable?: boolean;
  showReReplyWrite?: boolean;
  showReReplyCount?: boolean;
  showMyReplyStyle?: boolean;
  showContentInfo?: boolean;
  showPinned?: boolean;
  emptyDescription?: string;
  emptyTitle?: string;
}

export const ReplyList = ({
  className,
  replies,
  profiles = {},
  contents = {},
  episodes = {},
  likeReplyIds = [],
  parent,
  onClickReReply,
  onClickReply,
  focusedReplyId,
  myReplyEditable = false,
  myReplyDeletable = false,
  showReReplyWrite = false,
  showReReplyCount = false,
  showMyReplyStyle = false,
  showContentInfo = false,
  showPinned = false,
  emptyDescription,
  emptyTitle,
}: RepliesProps) => {
  const { authorProfile } = useEpisodeReplyState();
  const { dispatchReplyAction } = useEpisodeReplyDispatch();
  const blockedProfileIds = useBlockProfiles();

  const handleAction = useCallback(
    async (action: ReplyEditAction, reply: ReplyRelayDto, onMutate?: OnMutateFn) =>
      dispatchReplyAction(action, { reply, onMutate }),
    [dispatchReplyAction],
  );

  const render = useCallback(
    (reply: ReplyRelayDto) => {
      if (reply.profileId && blockedProfileIds.has(reply.profileId)) {
        return null;
      }

      return (
        <li key={reply.id}>
          {reply.isDeleted || reply.blockedType ? (
            <BlockedOrDeletedReply
              reply={reply as ReplyDeletedRelayDto | ReplyBlockedRelayDto}
              episode={episodes?.[reply.episodeId]}
              authorProfile={authorProfile}
              showReReplyCount={showReReplyCount}
              onClickReReply={onClickReReply}
            />
          ) : (
            <Reply
              isFocused={focusedReplyId === reply.id}
              parentReplyId={parent}
              reply={reply}
              profile={profiles[reply.profileId]}
              content={reply.contentId ? contents[reply.contentId] : undefined}
              episode={reply.episodeId ? episodes[reply.episodeId] : undefined}
              isLiked={likeReplyIds.includes(reply.id)}
              isAuthorReply={!!(authorProfile && authorProfile.id === reply.profileId)}
              showMenuButton
              myReplyEditable={myReplyEditable}
              myReplyDeletable={myReplyDeletable}
              showMyReplyStyle={showMyReplyStyle}
              showReReplyWrite={showReReplyWrite}
              showReReplyCount={showReReplyCount}
              showContentInfo={showContentInfo}
              showPinned={showPinned}
              onDispatchAction={handleAction}
              onClickReply={onClickReply}
              authorProfile={authorProfile}
              onClickReReply={onClickReReply}
            />
          )}
        </li>
      );
    },
    [
      blockedProfileIds,
      episodes,
      authorProfile,
      showReReplyCount,
      focusedReplyId,
      parent,
      profiles,
      contents,
      likeReplyIds,
      myReplyEditable,
      myReplyDeletable,
      showMyReplyStyle,
      showReReplyWrite,
      showContentInfo,
      showPinned,
      handleAction,
      onClickReply,
      onClickReReply,
    ],
  );

  return (
    <StyledContainer className={className}>
      {replies.length === 0 && (
        <li>
          <EmptyList title={emptyTitle} description={emptyDescription} />
        </li>
      )}

      {replies.map(render)}
    </StyledContainer>
  );
};

const StyledContainer = styled.ul`
  height: 100%;
`;
